/** Входящие — SPEC-2 §2. */

import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { BookOpen, FileText, Lightbulb, Link as LinkIcon, MessageCircle } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";

import { Ideas, Me, Methods, Notes, Tasks } from "@/api/resources";
import type { IdeaOut, MethodOut, NoteOut } from "@/api/types";
import { AuthorLabel } from "@/components/AuthorLabel";
import { IdeaWorkLinks } from "@/components/IdeaWork";
import { WithdrawIdea } from "@/components/WithdrawIdea";
import { CaptureBar } from "@/components/CaptureBar";
import { ChatThread } from "@/components/ChatThread";
import { MethodStatusBadge } from "@/components/MethodBadges";
import { Badge, FilterChip, IdeaStatusBadge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Card, ClickableCard } from "@/components/ui/Card";
import { EmptyState } from "@/components/ui/EmptyState";
import { TextArea } from "@/components/ui/Field";
import { CollapsibleSection, ScreenTitle, Section } from "@/components/ui/Section";
import { Sheet } from "@/components/ui/Sheet";
import { SkeletonCard, SkeletonList } from "@/components/ui/Skeleton";
import { useToast } from "@/components/ui/Toast";
import { formatRelative, parseDate } from "@/lib/format";
import { useBackButton } from "@/lib/telegram-ui";

type Filter = "all" | "ideas" | "materials" | "drafts" | "review";

const FILTERS: { value: Filter; label: string }[] = [
  { value: "all", label: "все" },
  { value: "ideas", label: "идеи" },
  { value: "materials", label: "материалы" },
  { value: "drafts", label: "черновики" },
  { value: "review", label: "на ревью" },
];

const MATERIAL_TAG = "material";
const URL_RE = /https?:\/\/\S+/;

type InboxItem =
  | { kind: "idea"; at: string; idea: IdeaOut }
  | { kind: "material"; at: string; note: NoteOut }
  | { kind: "draft"; at: string; method: MethodOut };

export function InboxView() {
  // Spec 020 D5: no longer a tab — opened from «Сейчас».
  useBackButton("/");
  const qc = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate();
  const [filter, setFilter] = useState<Filter>("all");
  const [acting, setActing] = useState<InboxItem | null>(null);

  const ideas = useQuery({
    queryKey: ["ideas", "open"],
    queryFn: async () => {
      const [fresh, reviewing] = await Promise.all([Ideas.list("new"), Ideas.list("reviewing")]);
      return [...fresh, ...reviewing];
    },
  });
  const notes = useQuery({ queryKey: ["notes"], queryFn: Notes.list });
  const drafts = useQuery({
    queryKey: ["methods", "draft"],
    queryFn: () => Methods.list({ status: "draft", limit: 50 }),
  });

  const today = useMemo(
    () => (notes.data ?? []).find((n) => n.is_daily),
    [notes.data],
  );

  const items = useMemo<InboxItem[]>(() => {
    const rows: InboxItem[] = [
      ...(ideas.data ?? []).map((idea) => ({
        kind: "idea" as const,
        at: idea.created_at,
        idea,
      })),
      // Materials are notes tagged `material` — the same shape M2 indexes for
      // search, so what shows up here is exactly what will be findable later.
      ...(notes.data ?? [])
        .filter((n) => n.tags.includes(MATERIAL_TAG))
        .map((note) => ({ kind: "material" as const, at: note.created_at, note })),
      ...(drafts.data ?? []).map((method) => ({
        kind: "draft" as const,
        at: method.created_at,
        method,
      })),
    ];
    return rows.sort((a, b) => b.at.localeCompare(a.at));
  }, [ideas.data, notes.data, drafts.data]);

  const visible = items.filter((item) => {
    switch (filter) {
      case "ideas":
        return item.kind === "idea";
      case "materials":
        return item.kind === "material";
      case "drafts":
        return item.kind === "draft";
      case "review":
        return item.kind === "idea" && item.idea.status === "reviewing";
      case "all":
      default:
        return true;
    }
  });

  const loading = ideas.isLoading || notes.isLoading || drafts.isLoading;
  const failed = ideas.isError || notes.isError || drafts.isError;

  const refetchAll = () => {
    void ideas.refetch();
    void notes.refetch();
    void drafts.refetch();
  };

  return (
    <div className="space-y-4 pt-2">
      <ScreenTitle
        eyebrow="Что прилетело и ещё не разобрано"
        trailing={
          <Link to="/ideas" className="text-meta text-accent">
            Все идеи
          </Link>
        }
      >
        Входящие
      </ScreenTitle>

      <CaptureBar onCaptured={refetchAll} />

      {today && (
        <CollapsibleSection title="Сегодня" icon={<FileText className="h-[18px] w-[18px] text-hint" aria-hidden="true" />}>
          <TodayCard note={today} />
        </CollapsibleSection>
      )}

      <div className="-mx-4 flex gap-2 overflow-x-auto px-4 pb-1">
        {FILTERS.map(({ value, label }) => (
          <FilterChip key={value} active={filter === value} onClick={() => setFilter(value)}>
            {label}
          </FilterChip>
        ))}
      </div>

      <Section>
        {loading && (
          <SkeletonList count={3} />
        )}

        {failed && !loading && (
          <Card className="space-y-2">
            <p className="text-cell text-text">Не получилось загрузить входящие.</p>
            <Button size="compact" variant="secondary" onClick={refetchAll}>
              Повторить
            </Button>
          </Card>
        )}

        {!loading && !failed && visible.length === 0 && (
          <EmptyState
            icon={<Lightbulb className="h-8 w-8" aria-hidden="true" />}
            title={filter === "all" ? "Входящие пусты" : "В этом фильтре пусто"}
            description={
              filter === "all"
                ? "Всё разобрано. Новая мысль — в поле сверху."
                : "Попробуй другой фильтр."
            }
          />
        )}

        {!loading &&
          !failed &&
          visible.map((item) => (
            <InboxRow key={rowKey(item)} item={item} onAct={() => setActing(item)} />
          ))}
      </Section>

      {acting && <InboxActionsSheet
        key={rowKey(acting)}
        item={acting}
        onClose={() => setActing(null)}
        onDone={(message) => {
          setActing(null);
          toast.success(message);
          qc.invalidateQueries({ queryKey: ["ideas"] });
          qc.invalidateQueries({ queryKey: ["methods"] });
          qc.invalidateQueries({ queryKey: ["notes"] });
        }}
        onGoToMethod={(id) => {
          setActing(null);
          navigate(`/knowledge/${id}`);
        }}
      />}
    </div>
  );
}

function rowKey(item: InboxItem): string {
  if (item.kind === "idea") return `i-${item.idea.id}`;
  if (item.kind === "material") return `n-${item.note.id}`;
  return `m-${item.method.id}`;
}

function TodayCard({ note }: { note: NoteOut }) {
  const body = note.body_md.trim();
  return (
    <Card>
      {body ? (
        <p className="whitespace-pre-wrap text-cell text-text">{body}</p>
      ) : (
        <p className="text-meta text-hint">Сегодня пока пусто.</p>
      )}
    </Card>
  );
}

function InboxRow({ item, onAct }: { item: InboxItem; onAct: () => void }) {
  const at = parseDate(item.at);

  if (item.kind === "idea") {
    const { idea } = item;
    const excerpt = idea.summary || idea.body_md.split("\n", 1)[0] || idea.original_text;
    return (
      <Card className="space-y-2">
        <div className="flex items-start gap-2">
          <Lightbulb className="mt-0.5 h-[18px] w-[18px] shrink-0 text-hint" aria-hidden="true" />
          <p className="min-w-0 flex-1 text-cell text-text">{excerpt}</p>
          <IdeaStatusBadge status={idea.status} />
        </div>
        <div className="flex items-center justify-between gap-2">
          <div className="min-w-0 flex-1 space-y-0.5">
            <p><AuthorLabel authorId={idea.author_id} /></p>
            {at && <p className="text-meta text-hint">{formatRelative(at)}</p>}
          </div>
          <Button size="compact" variant="secondary" className="shrink-0" onClick={onAct}>
            Что делаем
          </Button>
        </div>
      </Card>
    );
  }

  if (item.kind === "material") {
    const { note } = item;
    const url = note.body_md.match(URL_RE)?.[0];
    return (
      <Card className="space-y-2">
        <div className="flex items-start gap-2">
          {url ? (
            <LinkIcon className="mt-0.5 h-[18px] w-[18px] shrink-0 text-hint" aria-hidden="true" />
          ) : (
            <FileText className="mt-0.5 h-[18px] w-[18px] shrink-0 text-hint" aria-hidden="true" />
          )}
          <div className="min-w-0 flex-1">
            <p className="text-cell text-text">{note.title || "Материал"}</p>
            {url && <p className="truncate text-meta text-hint">{url}</p>}
          </div>
          <Badge tone="neutral">{note.is_shared ? "общий материал" : "личная заметка"}</Badge>
        </div>
        {at && <span className="text-meta text-hint">{formatRelative(at)}</span>}
        <Link to={`/knowledge/sources/note/${note.id}`} className="inline-flex min-h-touch items-center text-meta text-accent">Открыть материал</Link>
      </Card>
    );
  }

  const { method } = item;
  return (
    <ClickableCard to={`/knowledge/${method.id}`}>
      <div className="flex items-start gap-2">
        <BookOpen className="mt-0.5 h-[18px] w-[18px] shrink-0 text-hint" aria-hidden="true" />
        <div className="min-w-0 flex-1">
          <p className="text-cell text-text">{method.title}</p>
          {method.summary && <p className="text-meta text-hint">{method.summary}</p>}
        </div>
        <MethodStatusBadge status={method.status} />
      </div>
    </ClickableCard>
  );
}

/**
 * Discuss an unprocessed idea or choose what to do with it (SPEC-2 §2).
 *
 * "Сохранить как метод" goes through `source_idea_id`, so the method keeps a
 * link back to the thought it came from — the trail is what makes a method
 * auditable later. "Связать с задачей" records a durable source link so the
 * task keeps access to the original idea, discussion and files.
 */
function InboxActionsSheet({
  item,
  onClose,
  onDone,
  onGoToMethod,
}: {
  item: InboxItem | null;
  onClose: () => void;
  onDone: (message: string) => void;
  onGoToMethod: (methodId: string) => void;
}) {
  const toast = useToast();
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState("");
  const [linking, setLinking] = useState(false);
  const [discussing, setDiscussing] = useState(false);

  const activeTasks = useQuery({
    queryKey: ["tasks", "active"],
    queryFn: () => Tasks.listActive(20),
    enabled: linking,
  });

  const idea = item?.kind === "idea" ? item.idea : null;
  const me = useQuery({ queryKey: ["me"], queryFn: Me.get });
  const isOwn = me.data?.id === idea?.author_id;
  const canReview = !!me.data && !isOwn;

  const toMethod = useMutation({
    mutationFn: () =>
      Methods.create({
        title: (idea!.summary || idea!.original_text).slice(0, 300),
        first_version: { body_md: idea!.body_md || idea!.original_text },
        source_idea_id: idea!.id,
      }),
    onSuccess: (method) => onGoToMethod(method.id),
    onError: (e: Error) => toast.error(e.message),
  });

  const approve = useMutation({
    mutationFn: () => Ideas.approve(idea!.id, { version: idea!.version }),
    onSuccess: () => onDone("Идея стала задачей"),
    onError: (e: Error) => toast.error(e.message),
  });

  const reject = useMutation({
    mutationFn: () => Ideas.reject(idea!.id, reason.trim()),
    onSuccess: () => {
      setRejecting(false);
      setReason("");
      onDone("Идея отклонена");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const linkToTask = useMutation({
    mutationFn: (taskId: string) =>
      Ideas.linkTask(idea!.id, taskId, idea!.version),
    onSuccess: () => {
      setLinking(false);
      onDone("Идея связана с задачей");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  if (!idea) return null;

  return (
    <Sheet
      open
      onClose={onClose}
      title={discussing ? "Обсуждение идеи" : "Что делаем с идеей"}
      footer={discussing && <Button variant="secondary" block onClick={() => setDiscussing(false)}>Назад к действиям</Button>}
    >
      <div className="mb-3 space-y-1">
        <p className="text-cell text-text">{idea.summary || idea.original_text}</p>
        <p><AuthorLabel authorId={idea.author_id} /></p>
        <IdeaWorkLinks ideaId={idea.id} />
      </div>

      {discussing ? (
        <div className="space-y-3">
          <p className="text-meta text-hint">Сообщения сохраняются у этой идеи и видны в общем чате.</p>
          <ChatThread context={{ parent_type: "idea", parent_id: idea.id }} />
        </div>
      ) : rejecting ? (
        <div className="space-y-2">
          <TextArea
            label="Причина отклонения"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Почему отклоняем? Причина обязательна — её читают, когда идея всплывает снова."
            rows={4}
            autoFocus
          />
          <div className="flex gap-2">
            <Button
              variant="destructive"
              block
              loading={reject.isPending}
              disabled={!reason.trim()}
              onClick={() => reason.trim() && reject.mutate()}
            >
              Отклонить
            </Button>
            <Button variant="secondary" onClick={() => setRejecting(false)}>
              Назад
            </Button>
          </div>
        </div>
      ) : linking ? (
        <div className="space-y-2">
          {activeTasks.isLoading && <SkeletonCard />}
          {(activeTasks.data ?? []).map((task) => (
            <Button
              key={task.id}
              variant="secondary"
              block
              loading={linkToTask.isPending}
              onClick={() => linkToTask.mutate(task.id)}
            >
              {task.title}
            </Button>
          ))}
          {activeTasks.data?.length === 0 && (
            <p className="text-meta text-hint">Активных задач нет — привязывать не к чему.</p>
          )}
          <Button variant="ghost" block onClick={() => setLinking(false)}>
            Назад
          </Button>
        </div>
      ) : (
        <div className="space-y-2">
          <Button variant="secondary" block icon={<MessageCircle className="h-5 w-5" aria-hidden="true" />} onClick={() => setDiscussing(true)}>
            Начать обсуждение
          </Button>
          <Button
            variant="secondary"
            block
            loading={toMethod.isPending}
            onClick={() => toMethod.mutate()}
          >
            Сохранить как метод
          </Button>
          {canReview && <><Button variant="secondary" block onClick={() => setLinking(true)}>
            Связать с задачей
          </Button>
          <Button variant="strong" block loading={approve.isPending} onClick={() => approve.mutate()}>
            В задачу
          </Button>
          <Button variant="destructive-outline" block onClick={() => setRejecting(true)}>
            Отклонить
          </Button>
          </>}
          {isOwn && <WithdrawIdea idea={idea} onDone={() => onDone("Идея отозвана автором")} />}
        </div>
      )}
    </Sheet>
  );
}
